import { Flex, FlexProps, Spinner, Text } from "@chakra-ui/react";
// v5: Expressions was removed, define URL regex inline
const URL_REGEX = /https?:\/\/[^\s]+/g;
import { TimelineLoader } from "applesauce-loaders/loaders";
import { NostrEvent } from "nostr-tools";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import useRouteSearchValue from "../../hooks/use-route-search-value";
import { useTimelineCurserIntersectionCallback } from "../../hooks/use-timeline-cursor-intersection-callback";
import IntersectionObserverProvider from "../../providers/local/intersection-observer";
import LoadMoreButton from "../timeline/load-more-button";
import VerticalPageLayout from "../vertical-page-layout";
import type { FeedSortMode } from "../feed-sort-tabs";
import GenericNoteTimeline from "./generic-note-timeline";
import HotNoteTimeline from "./hot-note-timeline";
import MediaTimeline from "./media-timeline";

export function useTimelinePageEventFilter() {
  const [params] = useSearchParams();
  const view = params.get("view");

  return useCallback(
    (event: NostrEvent) => {
      if (view === "images" && !event.content.match(URL_REGEX)) return false;
      return true;
    },
    [view],
  );
}

export type TimelineViewType = "timeline" | "images";

export default function TimelinePage({
  loader,
  timeline,
  header,
  sortMode,
  ...props
}: { loader?: TimelineLoader; timeline: NostrEvent[]; header?: React.ReactNode; sortMode?: FeedSortMode } & Omit<
  FlexProps,
  "children" | "direction" | "gap"
>) {
  const callback = useTimelineCurserIntersectionCallback(loader);

  const viewParam = useRouteSearchValue("view", "timeline");
  const mode = (viewParam.value as TimelineViewType) ?? "timeline";

  const renderTimeline = () => {
    if (sortMode === "hot" && mode === "timeline") {
      return <HotNoteTimeline timeline={timeline} />;
    }
    switch (mode) {
      case "timeline":
        return <GenericNoteTimeline timeline={timeline} />;

      case "images":
        return <MediaTimeline timeline={timeline} />;
      default:
        return null;
    }
  };

  return (
    <IntersectionObserverProvider callback={callback}>
      <VerticalPageLayout mx="auto" gap="4" {...props}>
        {header}
        {timeline.length === 0 && loader && mode === "timeline" && (
          <Flex direction="column" alignItems="center" gap="2" py="8" color="GrayText">
            <Spinner size="md" />
            <Text fontSize="sm">Connecting to relay...</Text>
          </Flex>
        )}
        {timeline.length === 0 && mode === "images" && (
          <Text textAlign="center" py="8" color="GrayText" fontSize="sm">
            No images to show
          </Text>
        )}
        {renderTimeline()}
        {timeline.length > 0 && <LoadMoreButton loader={loader} />}
      </VerticalPageLayout>
    </IntersectionObserverProvider>
  );
}
